// A small synthesized drum kit mapped to General MIDI percussion keys 35–59.

import { Biquad, TWO_PI, addDampedSine, dcBlock, fadeOut, newPcm, noiseGen, normalize } from './dsp';
import type { Pcm } from './sprite';

/** GM percussion keys covered by the kit. */
export const DRUM_KEYS: readonly number[] = Array.from({ length: 25 }, (_, i) => 35 + i);

export const GM_DRUM_NAMES: Readonly<Record<number, string>> = {
  35: 'Acoustic bass drum',
  36: 'Bass drum',
  37: 'Side stick',
  38: 'Acoustic snare',
  39: 'Hand clap',
  40: 'Electric snare',
  41: 'Low floor tom',
  42: 'Closed hi-hat',
  43: 'High floor tom',
  44: 'Pedal hi-hat',
  45: 'Low tom',
  46: 'Open hi-hat',
  47: 'Low-mid tom',
  48: 'Hi-mid tom',
  49: 'Crash cymbal 1',
  50: 'High tom',
  51: 'Ride cymbal 1',
  52: 'Chinese cymbal',
  53: 'Ride bell',
  54: 'Tambourine',
  55: 'Splash cymbal',
  56: 'Cowbell',
  57: 'Crash cymbal 2',
  58: 'Vibraslap',
  59: 'Ride cymbal 2',
};

const PEAK = 0.85;

/** Relative loudness of each kit piece (cymbals and hats sit below drums). */
const LEVEL: Readonly<Record<number, number>> = {
  37: 0.7, 39: 0.8, 42: 0.5, 44: 0.45, 46: 0.5, 49: 0.6, 51: 0.5, 52: 0.6, 53: 0.55, 54: 0.5, 55: 0.55, 56: 0.55, 57: 0.6, 58: 0.5, 59: 0.5,
};

/** Sine with an exponential pitch sweep and amplitude decay (kicks, toms). */
function sweptSine(out: Float32Array, sr: number, fStart: number, fEnd: number, sweep: number, decay: number, amp: number): void {
  let ph = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const f = fEnd + (fStart - fEnd) * Math.exp(-t / sweep);
    ph += (TWO_PI * f) / sr;
    out[i] += amp * Math.sin(ph) * Math.exp(-t / decay);
  }
}

/** Filtered noise with an exponential decay added into `out`. */
function noiseBurst(out: Float32Array, sr: number, rnd: () => number, hp: number, lp: number, decay: number, amp: number, start = 0, attack = 0.0005): void {
  const h = Biquad.highpass(sr, hp);
  const l = Biquad.lowpass(sr, lp);
  const a = Math.max(1, attack * sr);
  for (let i = start; i < out.length; i++) {
    const t = (i - start) / sr;
    const env = Math.exp(-t / decay) * Math.min(1, (i - start) / a);
    if (env < 1e-4 && t > 0.01) break;
    out[i] += amp * env * l.process(h.process(rnd()));
  }
}

/** Metallic tone: six detuned square waves (cymbals, hats, cowbell). */
function metal(out: Float32Array, sr: number, base: number, ratios: readonly number[], decay: number, amp: number, hp: number, lp: number): void {
  const h = Biquad.highpass(sr, hp);
  const h2 = Biquad.highpass(sr, hp);
  const l = Biquad.lowpass(sr, lp);
  const phases = ratios.map(() => 0);
  const incs = ratios.map((r) => (base * r) / sr);
  for (let i = 0; i < out.length; i++) {
    let s = 0;
    for (let k = 0; k < phases.length; k++) {
      phases[k] += incs[k];
      if (phases[k] >= 1) phases[k] -= 1;
      s += phases[k] < 0.5 ? 1 : -1;
    }
    const env = Math.exp(-i / sr / decay);
    out[i] += amp * env * l.process(h2.process(h.process(s / phases.length)));
  }
}

const METAL_RATIOS = [1, 1.4836, 1.8, 2.546, 2.63, 3.897];

function kick(sr: number, rnd: () => number, deep: boolean): Pcm {
  const out = newPcm((deep ? 0.6 : 0.5) * sr);
  sweptSine(out, sr, deep ? 140 : 165, deep ? 42 : 50, deep ? 0.045 : 0.035, deep ? 0.22 : 0.18, 1);
  noiseBurst(out, sr, rnd, 1000, 5000, 0.003, 0.35);
  return out;
}

function snare(sr: number, rnd: () => number, electric: boolean): Pcm {
  const out = newPcm(0.45 * sr);
  addDampedSine(out, sr, electric ? 200 : 185, 0.55, 0.25, 0);
  addDampedSine(out, sr, electric ? 360 : 330, 0.35, 0.18, 0);
  noiseBurst(out, sr, rnd, electric ? 1500 : 1000, electric ? 10000 : 8000, electric ? 0.09 : 0.12, 0.9);
  return out;
}

function tom(sr: number, rnd: () => number, f: number): Pcm {
  const out = newPcm(0.7 * sr);
  sweptSine(out, sr, f * 1.6, f, 0.03, 0.28, 1);
  noiseBurst(out, sr, rnd, 200, 3000, 0.01, 0.2);
  return out;
}

function hat(sr: number, rnd: () => number, decay: number, len: number): Pcm {
  const out = newPcm(len * sr);
  metal(out, sr, 330, METAL_RATIOS, decay, 0.6, 7000, 14000);
  noiseBurst(out, sr, rnd, 7000, 15000, decay * 0.8, 0.5);
  return out;
}

function cymbal(sr: number, rnd: () => number, base: number, decay: number, len: number, noiseAmt: number, hp: number): Pcm {
  const out = newPcm(len * sr);
  metal(out, sr, base, METAL_RATIOS, decay, 0.5, hp, 15000);
  noiseBurst(out, sr, rnd, hp * 0.8, 14000, decay * 0.7, noiseAmt, 0, 0.002);
  return out;
}

function clap(sr: number, rnd: () => number): Pcm {
  const out = newPcm(0.35 * sr);
  for (const ms of [0, 11, 22]) noiseBurst(out, sr, rnd, 900, 3500, 0.006, 0.8, Math.round((ms / 1000) * sr));
  noiseBurst(out, sr, rnd, 900, 3500, 0.09, 0.5, Math.round(0.03 * sr));
  return out;
}

function sideStick(sr: number, rnd: () => number): Pcm {
  const out = newPcm(0.15 * sr);
  addDampedSine(out, sr, 820, 0.6, 0.06, 0);
  addDampedSine(out, sr, 1650, 0.3, 0.04, 0);
  noiseBurst(out, sr, rnd, 1500, 6000, 0.008, 0.7);
  return out;
}

function tambourine(sr: number, rnd: () => number): Pcm {
  const out = newPcm(0.4 * sr);
  metal(out, sr, 1500, [1, 1.31, 1.67, 2.13, 2.42, 2.9], 0.12, 0.4, 5000, 15000);
  noiseBurst(out, sr, rnd, 6000, 15000, 0.1, 0.6, 0, 0.004);
  return out;
}

function cowbell(sr: number): Pcm {
  const out = newPcm(0.45 * sr);
  metal(out, sr, 562, [1, 1.5036], 0.1, 1, 400, 4000);
  const bp = Biquad.bandpass(sr, 800, 1.5);
  bp.run(out);
  return out;
}

function vibraslap(sr: number, rnd: () => number): Pcm {
  const out = newPcm(0.9 * sr);
  const bp = Biquad.bandpass(sr, 3000, 2);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const rattle = Math.max(0, Math.sin(TWO_PI * 28 * t)) ** 8;
    out[i] = bp.process(rnd()) * rattle * Math.exp(-t / 0.3);
  }
  return out;
}

/** Render one GM percussion key (unknown keys fall back to the nearest kit piece). */
export function renderDrum(key: number, sr: number, seed: number): Pcm {
  const rnd = noiseGen(seed);
  let out: Pcm;
  switch (Math.round(key)) {
    case 35:
      out = kick(sr, rnd, true);
      break;
    case 36:
      out = kick(sr, rnd, false);
      break;
    case 37:
      out = sideStick(sr, rnd);
      break;
    case 38:
      out = snare(sr, rnd, false);
      break;
    case 39:
      out = clap(sr, rnd);
      break;
    case 40:
      out = snare(sr, rnd, true);
      break;
    case 41:
      out = tom(sr, rnd, 82);
      break;
    case 42:
      out = hat(sr, rnd, 0.045, 0.2);
      break;
    case 43:
      out = tom(sr, rnd, 98);
      break;
    case 44:
      out = hat(sr, rnd, 0.03, 0.15);
      break;
    case 45:
      out = tom(sr, rnd, 110);
      break;
    case 46:
      out = hat(sr, rnd, 0.35, 1);
      break;
    case 47:
      out = tom(sr, rnd, 131);
      break;
    case 48:
      out = tom(sr, rnd, 156);
      break;
    case 49:
      out = cymbal(sr, rnd, 380, 0.9, 2.2, 0.8, 4000);
      break;
    case 50:
      out = tom(sr, rnd, 185);
      break;
    case 51:
      out = cymbal(sr, rnd, 420, 0.7, 1.8, 0.25, 5000);
      break;
    case 52:
      out = cymbal(sr, rnd, 300, 0.6, 1.6, 1, 2500);
      break;
    case 53: {
      out = cymbal(sr, rnd, 420, 0.5, 1.4, 0.1, 5000);
      addDampedSine(out, sr, 2100, 0.25, 1.2, 0);
      addDampedSine(out, sr, 3150, 0.12, 0.9, 0);
      break;
    }
    case 54:
      out = tambourine(sr, rnd);
      break;
    case 55:
      out = cymbal(sr, rnd, 450, 0.35, 1, 0.8, 5000);
      break;
    case 56:
      out = cowbell(sr);
      break;
    case 57:
      out = cymbal(sr, rnd, 350, 1.1, 2.4, 0.8, 3500);
      break;
    case 58:
      out = vibraslap(sr, rnd);
      break;
    case 59:
      out = cymbal(sr, rnd, 400, 0.8, 2, 0.3, 4500);
      break;
    default:
      return renderDrum(key < 35 ? 35 : 59, sr, seed);
  }
  dcBlock(out, sr, 20);
  normalize(out, PEAK * (LEVEL[Math.round(key)] ?? 1));
  fadeOut(out, Math.round(out.length * 0.15));
  return out;
}
